/**
 * saveViaApi — save a list through our OWN domain when the browser is
 * blocking Firestore. Walter 2026-08-20; plan:
 * `docs/proposals/2026-08-20-surviving-content-blockers.md`.
 *
 * Content blockers filter on URL PATTERNS. Ghostery, Brave Shields, uBlock et
 * al. match `firestore.googleapis.com/...&zx=…` because the cache-buster looks
 * like a tracker parameter — Walter's console was full of
 * `net::ERR_BLOCKED_BY_CLIENT`, and a day of his edits never left the device.
 *
 * `/api/saveList` is a Firebase Hosting rewrite to a Cloud Function, so the
 * request goes to listicity.app: first-party, matching no filter rule. Same
 * conflict-aware write, different door.
 *
 * Used ONLY after a direct write fails, so an unblocked user never touches
 * it and pays nothing. Returns true only on a confirmed server write — the
 * caller falls back to the offline queue otherwise, exactly as before.
 */

import { auth } from './firebase';

const ENDPOINT = '/api/saveList';
const TIMEOUT_MS = 10000;

export async function saveViaApi({ ownerUid, boardId, listId, patch, baselineContent }) {
  try {
    const user = auth.currentUser;
    if (!user) return false;
    // A fresh-enough ID token; the function verifies it and re-checks the
    // same ACLs firestore.rules enforce (an Admin-SDK write bypasses rules).
    const token = await user.getIdToken();
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ ownerUid, boardId, listId, patch, baselineContent }),
      cache: 'no-store',
      signal: AbortSignal.timeout ? AbortSignal.timeout(TIMEOUT_MS) : undefined,
    });
    if (!res.ok) return false;
    const data = await res.json().catch(() => null);
    return !!(data && data.ok);
  } catch (_) {
    // Blocked here too, offline, or the functions layer is down — the caller
    // queues, which is the pre-existing behaviour.
    return false;
  }
}
